import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Form, 
  Input, 
  Select, 
  Button, 
  Space, 
  Alert,
  Typography,
  Divider
} from 'antd';
import { UserOutlined, MailOutlined, SafetyOutlined } from '@ant-design/icons';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { 
  SuperAdminRole, 
  getSuperAdminRoleLabel,
  getInvitableRoles
} from '@municipality/types';
import { useAuth } from '../hooks/useAuth';

const { Title, Text } = Typography;
const { Option } = Select;

interface SuperAdminUserFormValues {
  email: string;
  name: string;
  surname: string;
  phoneNumber: string;
  superAdminRole: SuperAdminRole;
  status: 'active' | 'inactive' | 'pending';
}

interface SuperAdminUserFormProps {
  user?: {
    id: string;
    email: string;
    name?: string;
    surname?: string;
    phoneNumber?: string;
    superAdminRole?: SuperAdminRole;
    status?: 'active' | 'inactive' | 'pending';
  } | null;
  onSubmit: (values: SuperAdminUserFormValues) => Promise<void>;
  loading?: boolean;
  onCancel: () => void;
}

const SuperAdminUserForm: React.FC<SuperAdminUserFormProps> = ({
  user,
  onSubmit,
  loading = false,
  onCancel
}) => {
  const { t } = useTranslation('users');
  const { user: currentUser } = useAuth();
  const [form] = Form.useForm();
  const isEditing = !!user;

  // Get current user's super admin role
  const currentUserRole = currentUser?.superAdminRole as SuperAdminRole | undefined;

  // Determine which roles current user can invite
  const invitableRoles = currentUserRole 
    ? getInvitableRoles(currentUserRole) 
    : [];

  useEffect(() => {
    if (user && isEditing) {
      form.setFieldsValue({
        email: user.email,
        name: user.name || '',
        surname: user.surname || '',
        phoneNumber: user.phoneNumber || '',
        superAdminRole: user.superAdminRole || SuperAdminRole.CONTENT_MANAGER,
        status: user.status || 'active',
      });
    } else {
      form.resetFields();
    }
  }, [user, isEditing, form]);

  const handleSubmit = async (values: SuperAdminUserFormValues) => {
    try {
      await onSubmit(values);
      form.resetFields();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  // Role options with descriptions and role-based filtering
  const roleOptions = [
    {
      value: SuperAdminRole.SUPER_ADMIN,
      label: getSuperAdminRoleLabel(SuperAdminRole.SUPER_ADMIN),
      description: t('roles.superAdminDescription', 'Full system access and user management'),
      icon: <SafetyOutlined style={{ color: '#ff4d4f' }} />
    },
    {
      value: SuperAdminRole.ADMINISTRATOR,
      label: getSuperAdminRoleLabel(SuperAdminRole.ADMINISTRATOR),
      description: t('roles.administratorDescription', 'Manage users and municipalities'),
      icon: <SafetyOutlined style={{ color: '#ff7a45' }} />
    },
    {
      value: SuperAdminRole.REGION_MANAGER,
      label: getSuperAdminRoleLabel(SuperAdminRole.REGION_MANAGER),
      description: t('roles.regionManagerDescription', 'Manage content managers and support in assigned regions'),
      icon: <UserOutlined style={{ color: '#fa8c16' }} />
    },
    {
      value: SuperAdminRole.CONTENT_MANAGER,
      label: getSuperAdminRoleLabel(SuperAdminRole.CONTENT_MANAGER),
      description: t('roles.contentManagerDescription', 'Create and manage municipality content'),
      icon: <UserOutlined style={{ color: '#1890ff' }} />
    },
    {
      value: SuperAdminRole.ACCOUNTANT,
      label: getSuperAdminRoleLabel(SuperAdminRole.ACCOUNTANT),
      description: t('roles.accountantDescription', 'View financial data and analytics'),
      icon: <UserOutlined style={{ color: '#52c41a' }} />
    },
    {
      value: SuperAdminRole.SUPPORT,
      label: getSuperAdminRoleLabel(SuperAdminRole.SUPPORT),
      description: t('roles.supportDescription', 'Handle user support and issues'),
      icon: <UserOutlined style={{ color: '#722ed1' }} />
    },
  ].filter(option => invitableRoles.includes(option.value));

  return (
    <div style={{ padding: 24 }}>
      <Title level={4}>
        {isEditing ? t('editUser', 'Edit user') : t('inviteNewUser', 'Invite new user')}
      </Title>
      
      <Text type="secondary">
        {isEditing 
          ? t('editUserDescription', 'Update user information and access level')
          : t('inviteUserDescription', 'Send an invitation to a new super admin user')
        }
      </Text>

      <Divider />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          status: 'active',
          phoneNumber: '',
        }}
        requiredMark={false}
      >
        <Form.Item
          name="email"
          label={t('form.email', 'Email')}
          rules={[
            { required: true, message: t('form.emailRequired', 'Email is required') },
            { type: 'email', message: t('form.emailInvalid', 'Please enter a valid email') }
          ]}
        >
          <Input
            prefix={<MailOutlined />}
            placeholder={t('form.emailPlaceholder', 'Enter email address')}
            disabled={isEditing} // Email cannot be changed after user creation
          />
        </Form.Item>

        <Form.Item
          name="name"
          label={t('form.firstName', 'Name')}
          rules={[
            { required: true, message: t('form.firstNameRequired', 'Name is required') },
            { min: 2, message: t('form.firstNameMinLength', 'Name must be at least 2 characters') },
            { max: 50, message: t('form.firstNameMaxLength', 'Name must not exceed 50 characters') }
          ]}
        >
          <Input
            prefix={<UserOutlined />}
            placeholder={t('form.firstNamePlaceholder', 'Enter first name')}
          />
        </Form.Item>

        <Form.Item
          name="surname"
          label={t('form.lastName', 'Surname')}
          rules={[
            { required: true, message: t('form.lastNameRequired', 'Surname is required') },
            { min: 2, message: t('form.lastNameMinLength', 'Surname must be at least 2 characters') },
            { max: 50, message: t('form.lastNameMaxLength', 'Surname must not exceed 50 characters') }
          ]}
        >
          <Input
            prefix={<UserOutlined />}
            placeholder={t('form.lastNamePlaceholder', 'Enter last name')}
          />
        </Form.Item>

        <Form.Item
          name="phoneNumber"
          label={t('form.phoneNumber', 'Phone number')}
          rules={[
            { required: true, message: t('form.phoneNumberRequired', 'Phone number is required') }
          ]}
        >
          <PhoneInput
            country={'cy'}
            enableSearch={true}
            placeholder={t('form.phoneNumberPlaceholder', 'Enter phone number')}
            inputStyle={{
              width: '100%',
              height: '40px',
              fontSize: '14px',
            }}
            containerStyle={{
              width: '100%',
            }}
          />
        </Form.Item>

        <Form.Item
          name="superAdminRole"
          label={t('form.role', 'Role')}
          rules={[
            { required: true, message: t('form.roleRequired', 'Role is required') }
          ]}
        >
          <Select
            placeholder={t('form.rolePlaceholder', 'Select role')}
            optionLabelProp="label"
            size="large"
          >
            {roleOptions.map(option => (
              <Option 
                key={option.value} 
                value={option.value}
                label={option.label}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {option.icon}
                  <div>
                    <div style={{ fontWeight: 500 }}>{option.label}</div>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                      {option.description}
                    </div>
                  </div>
                </div>
              </Option>
            ))}
          </Select>
        </Form.Item>

        {isEditing && (
          <Form.Item
            name="status"
            label={t('form.status', 'Status')}
            rules={[
              { required: true, message: t('form.statusRequired', 'Status is required') }
            ]}
          >
            <Select placeholder={t('form.statusPlaceholder', 'Select status')}>
              <Option value="active">{t('form.active', 'Active')}</Option>
              <Option value="inactive">{t('form.inactive', 'Inactive')}</Option>
              <Option value="pending">{t('form.pending', 'Pending')}</Option>
            </Select>
          </Form.Item>
        )}

        {!isEditing && (
          <Alert
            message={t('invitationInfo.title', 'Invitation will be sent')}
            description={t('invitationInfo.description', 'An email invitation will be sent to the user with instructions to set their password and access the system.')}
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}

        {roleOptions.length === 0 && (
          <Alert
            message={t('noInvitableRoles.title', 'No invitable roles')}
            description={t('noInvitableRoles.description', 'You do not have permission to invite any users.')}
            type="warning"
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}

        <Form.Item style={{ marginBottom: 0 }}>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={onCancel}>
              {t('cancel', 'Cancel')}
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              icon={isEditing ? <UserOutlined /> : <MailOutlined />}
              disabled={roleOptions.length === 0}
            >
              {isEditing ? t('updateUser', 'Update user') : t('sendInvitation', 'Send invitation')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

export default SuperAdminUserForm;
